import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from './axios';
import type {
  DesktopArtifactUploadResponse,
  DesktopReleaseAdminListQuery,
  DesktopReleaseCreateRequest,
  DesktopReleaseUpdateRequest,
  DesktopReleaseDownloadUrlRequest,
  DesktopReleaseItem,
  DesktopReleaseLatestQuery,
  DesktopReleaseUploadQuery,
  DesktopReleaseUploadUrlRequest,
} from '@cms/models';
import { postArtifactMultipartUpload } from '@/lib/upload/multipart-api-upload';

type DesktopReleaseItemApiShape = DesktopReleaseItem & {
  publishedAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
};

export const desktopReleasesQueryKeys = {
  all: ['desktop-releases'] as const,
  list: (filters?: DesktopReleaseAdminListQuery) =>
    ['desktop-releases', 'list', filters ?? {}] as const,
};

export const desktopReleasesApi = {
  async list(filters?: DesktopReleaseAdminListQuery): Promise<DesktopReleaseItem[]> {
    const { data } = await apiClient.get<DesktopReleaseItemApiShape[]>('/desktop-releases', {
      params: filters ?? undefined,
    });
    return Array.isArray(data) ? data.map(d => ({ ...d })) : [];
  },

  async uploadUrl(payload: DesktopReleaseUploadUrlRequest): Promise<{
    uploadUrl: string;
    objectKey: string;
    expiresInSec: number;
  }> {
    const { data } = await apiClient.post('/desktop-releases/upload-url', payload);
    return data;
  },

  async uploadArtifact(
    query: DesktopReleaseUploadQuery,
    file: File,
    onProgress?: (percent: number) => void,
  ): Promise<DesktopArtifactUploadResponse> {
    return postArtifactMultipartUpload<DesktopArtifactUploadResponse>({
      path: '/desktop-releases/upload',
      file,
      query: {
        appSlug: query.appSlug,
        channel: query.channel,
        version: query.version,
        platform: query.platform,
        arch: query.arch,
        fileName: query.fileName,
      },
      onProgress,
    });
  },

  async createRelease(payload: DesktopReleaseCreateRequest): Promise<DesktopReleaseItem> {
    const { data } = await apiClient.post('/desktop-releases', payload);
    return data;
  },

  async updateRelease(releaseId: string, payload: DesktopReleaseUpdateRequest): Promise<DesktopReleaseItem> {
    const { data } = await apiClient.patch(`/desktop-releases/${releaseId}`, payload);
    return data;
  },

  async publishRelease(releaseId: string): Promise<DesktopReleaseItem> {
    const { data } = await apiClient.patch(`/desktop-releases/${releaseId}/publish`);
    return data;
  },

  async unpublishRelease(releaseId: string): Promise<DesktopReleaseItem> {
    const { data } = await apiClient.patch(`/desktop-releases/${releaseId}/unpublish`);
    return data;
  },

  async downloadUrl(payload: DesktopReleaseDownloadUrlRequest): Promise<{
    releaseId: string;
    downloadUrl: string;
    expiresInSec: number;
  }> {
    const { data } = await apiClient.post('/desktop-releases/download-url', payload);
    return data;
  },

  // Desktop API not used by web admin; kept for parity.
  async desktopLatest(query: DesktopReleaseLatestQuery): Promise<unknown> {
    const { data } = await apiClient.get('/desktop/releases/latest', { params: query });
    return data;
  },
};

export function useDesktopReleases(filters?: DesktopReleaseAdminListQuery) {
  return useQuery({
    queryKey: desktopReleasesQueryKeys.list(filters),
    queryFn: () => desktopReleasesApi.list(filters),
  });
}

export function usePublishDesktopRelease() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (releaseId: string) => desktopReleasesApi.publishRelease(releaseId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: desktopReleasesQueryKeys.all });
    },
  });
}

export function useUnpublishDesktopRelease() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (releaseId: string) => desktopReleasesApi.unpublishRelease(releaseId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: desktopReleasesQueryKeys.all });
    },
  });
}

export function useCreateDesktopRelease() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: DesktopReleaseCreateRequest) => desktopReleasesApi.createRelease(payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: desktopReleasesQueryKeys.all });
    },
  });
}

export function useUpdateDesktopRelease() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ releaseId, payload }: { releaseId: string; payload: DesktopReleaseUpdateRequest }) =>
      desktopReleasesApi.updateRelease(releaseId, payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: desktopReleasesQueryKeys.all });
    },
  });
}

export function useDesktopReleaseDownloadUrl() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: DesktopReleaseDownloadUrlRequest) => desktopReleasesApi.downloadUrl(payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: desktopReleasesQueryKeys.all });
    },
  });
}

export function useDesktopReleaseUploadUrl() {
  return useMutation({
    mutationFn: (payload: DesktopReleaseUploadUrlRequest) => desktopReleasesApi.uploadUrl(payload),
  });
}

